import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** One role + its org nodes on a user (as posted to the API). */
export interface UserRoleAssignment {
  role: number;
  org_level: number[];
}

/**
 * A user row as returned by the backend. Field names vary, so they're read via the
 * accessors below. The user-list design (Figma 632:4824) drives which fields the API
 * should provide: name, emp_id, email, contact, roles[], org hierarchy, status,
 * last-seen, and an optional avatar.
 */
export interface AppUser {
  id: number;
  fullName?: string | null;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
  contact?: string | string[] | null;
  phone?: string | null;
  phone_number?: string | null;
  emp_id?: string | null;
  created_at?: string | null;
  org_hierarchy?: string | null;
  hierarchy?: string | null;
  /** Org level names, when the API returns them on the user (not nested in roles). */
  org_level_names?: string[] | null;
  last_seen?: string | null;
  avatar?: string | null;
  photo?: string | null;
  image?: string | null;
  is_active?: boolean;
  active?: boolean;
  organization?: number | null;
  /** Directory-based create fields (Figma 1274-1621). */
  directory?: string | null;
  designation?: string | string[] | null;
  department?: string | string[] | null;
  /** The multi-value list columns (preferred over the joined `designation`/`department`). */
  designations?: string[] | null;
  departments?: string[] | null;
  location?: number | null;
  location_name?: string | null;
  /** EC level of the saved cascade location (round-tripped for edit re-reveal). */
  ec_location_level_id?: number | null;
  /** Non-core gazette fields kept verbatim by the backend (see CreateUserPayload). */
  profile?: Record<string, unknown> | null;
  roles?: Array<
    | string
    | {
        role?: number | { id: number; name?: string };
        role_name?: string;
        name?: string;
        org_level?: number | number[];
        org_level_name?: string;
        org_level_names?: string[];
      }
  >;
}

/** Result of a bulk user import (shape is best-effort; read defensively). */
export interface UserImportResult {
  created?: number;
  updated?: number;
  failed?: number;
  errors?: Array<string | { row?: number; message?: string }>;
  detail?: string;
}

/** Distinct designation/department values for an org — powers the user-list filters. */
export interface UserFieldOptions {
  designations: string[];
  departments: string[];
  /**
   * The selected department's own hierarchy (e.g. "police", "revenue") — present only when a
   * `department` is passed. Tells the create form to load that department's location tree
   * (organisation/node/tree?kind=<hierarchy_kind>) instead of the election tree.
   */
  hierarchy_kind?: string | null;
}

/** An org node option for the create-user dropdowns. */
export interface OrgNodeOption {
  id: number;
  org_name?: string;
  name?: string;
  level_name?: string;
  level_type?: number;
}

/** Request body for POST /access/users/ (directory-based create form, Figma 1274-1621). */
export interface CreateUserPayload {
  /** Which directory the person belongs to: elected | bureaucracy | party | organization. */
  directory: string;
  fullName: string;
  email: string;
  /** One or more contact numbers. */
  contact: string[];
  /** Selected position location — an org node id, or a territory id/code for the cascade. */
  location?: number | string | null;
  /** EC level rank of the cascade location (round-trips so edit can re-reveal the node). */
  ec_location_level_id?: number;
  /** Organization the user belongs to. */
  organization?: number;
  /** Selected role id (organization directory). */
  role?: number | null;
  /** Designation tags (one or more). */
  designation?: string[];
  /** Department tags (one or more) — bureaucracy / organization directories. */
  department?: string[];
  active: boolean;
  /** All non-core gazette fields (name parts, demographics, addresses, PAs, boundary,
   * etc.) — stored and returned by the backend as-is. Merged on PATCH. */
  profile?: Record<string, unknown>;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result the users table consumes. */
export interface UsersResponse {
  items: AppUser[];
  total: number;
  page: number;
  pageSize: number;
}

/* ------------------------------------------------------------------ *
 * Defensive accessors — the API's exact field names aren't guaranteed.
 * ------------------------------------------------------------------ */
export const userName = (u: AppUser) => u.fullName || u.full_name || u.name || '—';
export const userEmail = (u: AppUser) => u.email || '';
export const userPhone = (u: AppUser) => {
  const c = Array.isArray(u.contact) ? u.contact.filter(Boolean).join(', ') : u.contact;
  return c || u.phone || u.phone_number || '';
};
export const userActive = (u: AppUser) => u.is_active ?? u.active ?? true;
export const userEmpId = (u: AppUser) => u.emp_id || '';
export const userDirectory = (u: AppUser) => u.directory || '';
/** Designation as a display string — joins tags when the API returns an array. */
export const userDesignation = (u: AppUser) =>
  Array.isArray(u.designation) ? u.designation.filter(Boolean).join(', ') : u.designation || '';
/** Designation tags as an independent list. */
export const userDesignationList = (u: AppUser): string[] =>
  Array.isArray(u.designation)
    ? u.designation.filter(Boolean)
    : u.designation
      ? [u.designation]
      : [];
/** Department tags as an independent list. */
export const userDepartmentList = (u: AppUser): string[] =>
  Array.isArray(u.department)
    ? u.department.filter(Boolean)
    : u.department
      ? [u.department]
      : [];
export const userLastSeen = (u: AppUser) => u.last_seen || '';
export const userAvatar = (u: AppUser) => u.avatar || u.photo || u.image || '';
export const userRoleNames = (u: AppUser): string[] =>
  (u.roles ?? [])
    .map((r) => {
      if (typeof r === 'string') return r;
      if (typeof r?.role === 'object' && r.role) return r.role.name ?? '';
      return r?.role_name ?? r?.name ?? '';
    })
    .filter(Boolean);
/** The org levels a user belongs to. Prefers the per-role `org_level_names` array. */
export const userHierarchy = (u: AppUser): string => {
  const names = userHierarchyList(u);
  if (names.length) return names.join(', ');
  if (u.org_hierarchy) return u.org_hierarchy;
  if (u.hierarchy) return u.hierarchy;
  return '—';
};
/** The distinct org level names for a user (`org_level_names`) — top-level or per-role. */
export const userHierarchyList = (u: AppUser): string[] => {
  const names: string[] = [];
  // Top-level array, when the API returns it directly on the user.
  if (Array.isArray(u.org_level_names)) names.push(...u.org_level_names);
  // Otherwise gather the array from each role.
  if (names.length === 0) {
    names.push(
      ...(u.roles ?? []).flatMap((r) =>
        typeof r === 'object' && Array.isArray(r?.org_level_names) ? r.org_level_names : [],
      ),
    );
  }
  // Last resort: the singular `org_level_name` on each role.
  if (names.length === 0) {
    names.push(
      ...(u.roles ?? [])
        .map((r) => (typeof r === 'object' ? r?.org_level_name : undefined))
        .filter((v): v is string => Boolean(v)),
    );
  }
  return Array.from(new Set(names.filter(Boolean)));
};

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<
      UsersResponse,
      | {
          search?: string;
          isActive?: boolean | '';
          role?: number | '';
          organization?: number | '';
          orgLevel?: number | string | '';
          /** Directory filter: elected | non_elected | bureaucracy | party | organization. */
          directory?: string;
          /** Location filter — an org node id. */
          location?: number | string;
          /** Designation text filter. */
          designation?: string;
          /** Department text filter. */
          department?: string;
          /** Political Person outcome filter → election_result (e.g. "Winner (Incumbent / Elected)"). */
          candidateOutcome?: string;
          /** Political Person party-name filter. */
          partyName?: string;
          page?: number;
          pageSize?: number;
          /** Fetch the whole list without pagination (e.g. for a dropdown). */
          all?: boolean;
        }
      | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.users}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.isActive === true || arg?.isActive === false ? { is_active: arg.isActive } : {}),
          ...(arg?.role ? { role: arg.role } : {}),
          ...(arg?.organization ? { organization: arg.organization } : {}),
          ...(arg?.orgLevel ? { org_level: arg.orgLevel } : {}),
          ...(arg?.directory ? { directory: arg.directory } : {}),
          ...(arg?.location ? { location: arg.location } : {}),
          ...(arg?.designation ? { designation: arg.designation } : {}),
          ...(arg?.department ? { department: arg.department } : {}),
          ...(arg?.candidateOutcome ? { election_result: arg.candidateOutcome } : {}),
          ...(arg?.partyName ? { party_name: arg.partyName } : {}),
          // `all` skips pagination entirely; otherwise send page/page_size.
          ...(arg?.all ? {} : { page: arg?.page ?? 1, page_size: arg?.pageSize ?? 10 }),
        },
      }),
      transformResponse: (r: Listish<AppUser>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['Users'],
    }),
    getUser: build.query<AppUser, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.users}/${id}/` }),
      providesTags: ['Users'],
    }),
    // Look up a user by phone (auto-fill helpers) — returns the match or null.
    getUserByPhone: build.query<AppUser | null, { phone: string }>({
      query: ({ phone }) => ({ url: `${ApiEndPoints.users}/by-phone/`, params: { phone } }),
      transformResponse: (r: AppUser | AppUser[] | { results?: AppUser[] } | null) => {
        if (Array.isArray(r)) return r[0] ?? null;
        const results = (r as { results?: AppUser[] })?.results;
        if (Array.isArray(results)) return results[0] ?? null;
        return (r as AppUser) ?? null;
      },
    }),
    createUser: build.mutation<AppUser, CreateUserPayload>({
      query: (body) => ({
        url: `${ApiEndPoints.users}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: build.mutation<AppUser, { id: number | string; patch: Record<string, unknown> }>({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.users}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: build.mutation<void, number | string>({
      query: (id) => ({
        url: `${ApiEndPoints.users}/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    // Download the bulk-upload template (.xlsx, one sheet per directory) for an organization.
    getUserImportTemplate: build.query<Blob, { organization: number | string }>({
      query: ({ organization }) => ({
        url: `${ApiEndPoints.usersImportTemplate}/`,
        params: { organization },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    // Bulk-upload a filled template. Body is multipart/form-data with organization + file.
    importUsers: build.mutation<UserImportResult, { file: File; organization: number | string }>({
      query: ({ file, organization }) => {
        const form = new FormData();
        form.append('organization', String(organization));
        form.append('file', file);
        return { url: `${ApiEndPoints.usersImport}/`, method: 'POST', body: form };
      },
      invalidatesTags: ['Users'],
    }),
    // Org nodes for the create-user / admin-directory dropdowns. `kind="admin"` returns
    // the administrative-hierarchy nodes; `levelType` optionally filters to one level type.
    getOrgNodes: build.query<
      OrgNodeOption[],
      { organization: number | string; levelType?: number; kind?: string }
    >({
      query: ({ organization, levelType, kind }) => ({
        url: `${ApiEndPoints.organizationNode}/`,
        params: {
          organization,
          ...(levelType != null ? { level_type: levelType } : {}),
          ...(kind ? { kind } : {}),
        },
      }),
      transformResponse: (r: Listish<OrgNodeOption>) => asList(r),
    }),

    // Distinct designation/department values — scoped by organization and/or directory.
    getDesignation: build.query<
      UserFieldOptions,
      { organization?: number | string; directory?: string; department?: string } | number | string
    >({
      query: (arg) => {
        // Back-compat: a bare id means `{ organization }`.
        const a = typeof arg === 'object' ? arg : { organization: arg };
        return {
          url: `${ApiEndPoints.designations}/`,
          params: {
            ...(a.organization ? { organization: a.organization } : {}),
            ...(a.directory ? { directory: a.directory } : {}),
            // Scopes the designations to one department (and returns its hierarchy_kind).
            ...(a.department ? { department: a.department } : {}),
          },
        };
      },
      transformResponse: (r: Partial<UserFieldOptions> | null): UserFieldOptions => ({
        // Keep the backend order as-is — it returns the department's designations pre-ordered.
        designations: r?.designations ?? [],
        departments: r?.departments ?? [],
        hierarchy_kind: r?.hierarchy_kind ?? null,
      }),
      providesTags: ['Users'],
    }),

  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useLazyGetUserByPhoneQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetOrgNodesQuery,
  useLazyGetUserImportTemplateQuery,
  useImportUsersMutation,
  useGetDesignationQuery,
} = usersApi;
